// BounceHouseReservationPage.tsx
import React, { useState, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Navbar,
  Nav,
  Badge,
  ListGroup,
} from "react-bootstrap";
import {
  BalloonHeart,
  CalendarEvent,
  PeopleFill,
  GeoAltFill,
  TelephoneFill,
  EnvelopeFill,
  CashCoin,
  ShieldCheck,
  ClockHistory,
  HouseDoorFill,
} from "react-bootstrap-icons";

// Make sure you have: import "bootstrap/dist/css/bootstrap.min.css"; in your entry file.

interface ReservationFormData {
  eventType: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  surfaceType: string;
  powerAvailable: string;
  guests: string;
  unitType: string;
  addOns: string[];
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  zip: string;
  notes: string;
  acceptTerms: boolean;
}

const addOnOptions = [
  {
    id: "generator",
    label: "Power Generator",
    description: "For locations without easy access to power.",
    price: 49,
  },
  {
    id: "attendant",
    label: "On-site Attendant",
    description: "We provide a safety-monitored attendant during the event.",
    price: 75,
  },
  {
    id: "extraHour",
    label: "Extra Hour",
    description: "Add one additional hour of bounce time.",
    price: 35,
  },
];

const unitPrices: Record<string, number> = {
  classic: 189,
  combo: 249,
  obstacle: 399,
};

const BounceHouseReservationPage_2025_11_21_chat: React.FC = () => {
  const [data, setData] = useState<ReservationFormData>({
    eventType: "",
    eventDate: "",
    startTime: "",
    endTime: "",
    surfaceType: "",
    powerAvailable: "",
    guests: "",
    unitType: "",
    addOns: [],
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    zip: "",
    notes: "",
    acceptTerms: false,
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTermsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData((prev) => ({ ...prev, acceptTerms: e.target.checked }));
  };

  const handleAddOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    setData((prev) => {
      const current = new Set(prev.addOns);
      if (checked) {
        current.add(value);
      } else {
        current.delete(value);
      }
      return { ...prev, addOns: Array.from(current) };
    });
  };

  const estimatedTotal = useMemo(() => {
    const base = unitPrices[data.unitType] ?? 0;
    const addOnsTotal = data.addOns.reduce((sum, id) => {
      const found = addOnOptions.find((a) => a.id === id);
      return sum + (found ? found.price : 0);
    }, 0);
    return base + addOnsTotal;
  }, [data.unitType, data.addOns]);

  const estimatedDeposit = useMemo(() => {
    if (!estimatedTotal) return 0;
    return Math.round(estimatedTotal * 0.25); // e.g. 25% deposit
  }, [estimatedTotal]);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();

    if (!data.acceptTerms) {
      alert("Please accept the rental terms & safety rules before reserving.");
      return;
    }

    // In a real app, send `data` to your backend/API here.
    console.log("Reservation data:", data);
    alert("Thank you! Your reservation request has been submitted. 🎈");

    // Optionally reset:
    // setData({ ...initial state... });
  };

  return (
    <>
      {/* NAVBAR */}
      <Navbar bg="light" expand="lg" sticky="top" className="shadow-sm">
        <Container>
          <Navbar.Brand className="fw-bold">
            <BalloonHeart className="me-2" />
            HappyBounce Rentals
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="reservation-navbar" />
          <Navbar.Collapse id="reservation-navbar">
            <Nav className="ms-auto">
              <Nav.Link href="#reservation">Reservation Form</Nav.Link>
              <Nav.Link href="#summary">Summary</Nav.Link>
              <Nav.Link href="tel:18005551234">
                <Button size="sm" variant="outline-primary">
                  <TelephoneFill className="me-1" />
                  Call (800) 555-1234
                </Button>
              </Nav.Link>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* HEADER */}
      <section
        className="py-4"
        style={{
          background:
            "linear-gradient(135deg, rgba(13,110,253,0.08), rgba(255,193,7,0.18))",
        }}
      >
        <Container>
          <Row className="align-items-center gy-2">
            <Col md={8}>
              <h1 className="fw-bold mb-1">Reserve Your Bounce House</h1>
              <p className="mb-0 text-muted">
                Tell us about your event and we&apos;ll confirm availability,
                pricing, and send your booking details.
              </p>
            </Col>
            <Col
              md={4}
              className="text-md-end text-start mt-3 mt-md-0 small text-muted"
            >
              <CalendarEvent className="me-1" />
              Standard rentals: 4 hours of bounce time
              <br />
              <ClockHistory className="me-1" />
              Extra hours &amp; school/church discounts available
            </Col>
          </Row>
        </Container>
      </section>

      {/* MAIN CONTENT */}
      <section id="reservation" className="py-5">
        <Container>
          <Row className="gy-4">
            {/* LEFT: FORM */}
            <Col lg={8}>
              <Card className="shadow-sm border-0">
                <Card.Body>
                  <h3 className="fw-bold mb-3">Reservation Details</h3>
                  <Form onSubmit={handleSubmit}>
                    {/* EVENT INFO */}
                    <h5 className="mt-3 mb-2">Event Information</h5>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3" controlId="eventType">
                          <Form.Label>Event Type</Form.Label>
                          <Form.Select
                            name="eventType"
                            value={data.eventType}
                            onChange={handleInputChange}
                            required
                          >
                            <option value="">Choose an event type</option>
                            <option>Birthday Party</option>
                            <option>School Event</option>
                            <option>Church Event</option>
                            <option>Corporate Event</option>
                            <option>Community Festival</option>
                            <option>Other</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3" controlId="guests">
                          <Form.Label>
                            Approx. Number of Kids{" "}
                            <PeopleFill className="ms-1" />
                          </Form.Label>
                          <Form.Control
                            type="number"
                            min={1}
                            name="guests"
                            value={data.guests}
                            onChange={handleInputChange}
                            placeholder="e.g. 20"
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3" controlId="eventDate">
                          <Form.Label>
                            Event Date <CalendarEvent className="ms-1" />
                          </Form.Label>
                          <Form.Control
                            type="date"
                            name="eventDate"
                            value={data.eventDate}
                            onChange={handleInputChange}
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3" controlId="startTime">
                          <Form.Label>Start Time</Form.Label>
                          <Form.Control
                            type="time"
                            name="startTime"
                            value={data.startTime}
                            onChange={handleInputChange}
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3" controlId="endTime">
                          <Form.Label>End Time</Form.Label>
                          <Form.Control
                            type="time"
                            name="endTime"
                            value={data.endTime}
                            onChange={handleInputChange}
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3" controlId="surfaceType">
                          <Form.Label>Setup Surface</Form.Label>
                          <Form.Select
                            name="surfaceType"
                            value={data.surfaceType}
                            onChange={handleInputChange}
                            required
                          >
                            <option value="">Select surface</option>
                            <option>Grass</option>
                            <option>Concrete</option>
                            <option>Asphalt</option>
                            <option>Indoor Gym / Hall</option>
                          </Form.Select>
                          <Form.Text className="text-muted">
                            We&apos;ll confirm staking or sandbag requirements.
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3" controlId="powerAvailable">
                          <Form.Label>Power Access</Form.Label>
                          <Form.Select
                            name="powerAvailable"
                            value={data.powerAvailable}
                            onChange={handleInputChange}
                            required
                          >
                            <option value="">Is power available on site?</option>
                            <option value="yes">
                              Yes – standard outlet within 75 ft
                            </option>
                            <option value="no">
                              No – I may need a generator
                            </option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* UNIT SELECTION */}
                    <h5 className="mt-4 mb-2">Bounce House Selection</h5>
                    <Row className="gy-3">
                      <Col md={4}>
                        <Card
                          className={
                            "h-100 selectable-card" +
                            (data.unitType === "classic"
                              ? " border-primary border-2"
                              : "")
                          }
                          onClick={() =>
                            setData((prev) => ({ ...prev, unitType: "classic" }))
                          }
                          style={{ cursor: "pointer" }}
                        >
                          <Card.Body>
                            <h6 className="fw-bold mb-1">Classic Castle</h6>
                            <div className="small text-muted mb-1">
                              Up to 12 kids
                            </div>
                            <div className="fw-bold mb-1">
                              ${unitPrices.classic} / 4 hours
                            </div>
                            <Form.Check
                              type="radio"
                              id="unit-classic"
                              name="unitType"
                              checked={data.unitType === "classic"}
                              onChange={() =>
                                setData((prev) => ({
                                  ...prev,
                                  unitType: "classic",
                                }))
                              }
                              label="Select"
                            />
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={4}>
                        <Card
                          className={
                            "h-100 selectable-card" +
                            (data.unitType === "combo"
                              ? " border-primary border-2"
                              : "")
                          }
                          onClick={() =>
                            setData((prev) => ({ ...prev, unitType: "combo" }))
                          }
                          style={{ cursor: "pointer" }}
                        >
                          <Card.Body>
                            <Badge bg="primary" className="mb-1">
                              Most Popular
                            </Badge>
                            <h6 className="fw-bold mb-1">
                              Slide Combo Castle
                            </h6>
                            <div className="small text-muted mb-1">
                              Bounce + slide + hoop
                            </div>
                            <div className="fw-bold mb-1">
                              ${unitPrices.combo} / 4 hours
                            </div>
                            <Form.Check
                              type="radio"
                              id="unit-combo"
                              name="unitType"
                              checked={data.unitType === "combo"}
                              onChange={() =>
                                setData((prev) => ({
                                  ...prev,
                                  unitType: "combo",
                                }))
                              }
                              label="Select"
                            />
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={4}>
                        <Card
                          className={
                            "h-100 selectable-card" +
                            (data.unitType === "obstacle"
                              ? " border-primary border-2"
                              : "")
                          }
                          onClick={() =>
                            setData((prev) => ({
                              ...prev,
                              unitType: "obstacle",
                            }))
                          }
                          style={{ cursor: "pointer" }}
                        >
                          <Card.Body>
                            <h6 className="fw-bold mb-1">
                              Mega Obstacle Course
                            </h6>
                            <div className="small text-muted mb-1">
                              Great for schools &amp; big events
                            </div>
                            <div className="fw-bold mb-1">
                              ${unitPrices.obstacle} / 4 hours
                            </div>
                            <Form.Check
                              type="radio"
                              id="unit-obstacle"
                              name="unitType"
                              checked={data.unitType === "obstacle"}
                              onChange={() =>
                                setData((prev) => ({
                                  ...prev,
                                  unitType: "obstacle",
                                }))
                              }
                              label="Select"
                            />
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                    <Form.Text className="text-muted small">
                      Not sure which to choose? Pick any option and describe
                      your event—we can recommend the best fit.
                    </Form.Text>

                    {/* ADD-ONS */}
                    <h5 className="mt-4 mb-2">Add-ons (Optional)</h5>
                    <Row className="gy-2">
                      {addOnOptions.map((addOn) => (
                        <Col md={6} key={addOn.id}>
                          <Form.Check
                            type="checkbox"
                            id={`addon-${addOn.id}`}
                            value={addOn.id}
                            checked={data.addOns.includes(addOn.id)}
                            onChange={handleAddOnChange}
                            label={
                              <span>
                                {addOn.label} –{" "}
                                <strong>${addOn.price}</strong>
                                <br />
                                <span className="small text-muted">
                                  {addOn.description}
                                </span>
                              </span>
                            }
                          />
                        </Col>
                      ))}
                    </Row>

                    {/* CONTACT INFO */}
                    <h5 className="mt-4 mb-2">Contact & Location</h5>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3" controlId="name">
                          <Form.Label>Full Name</Form.Label>
                          <Form.Control
                            type="text"
                            name="name"
                            value={data.name}
                            onChange={handleInputChange}
                            placeholder="Your name"
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3" controlId="email">
                          <Form.Label>
                            Email <EnvelopeFill className="ms-1" />
                          </Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={data.email}
                            onChange={handleInputChange}
                            placeholder="you@example.com"
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3" controlId="phone">
                          <Form.Label>
                            Mobile Phone <TelephoneFill className="ms-1" />
                          </Form.Label>
                          <Form.Control
                            type="tel"
                            name="phone"
                            value={data.phone}
                            onChange={handleInputChange}
                            placeholder="(555) 555-5555"
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3" controlId="zip">
                          <Form.Label>ZIP Code</Form.Label>
                          <Form.Control
                            type="text"
                            name="zip"
                            value={data.zip}
                            onChange={handleInputChange}
                            placeholder="e.g. 80211"
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3" controlId="address">
                      <Form.Label>
                        Event Address <HouseDoorFill className="ms-1" />
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="address"
                        value={data.address}
                        onChange={handleInputChange}
                        placeholder="Street address where the unit will be set up"
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="city">
                      <Form.Label>
                        City <GeoAltFill className="ms-1" />
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="city"
                        value={data.city}
                        onChange={handleInputChange}
                        placeholder="City"
                        required
                      />
                    </Form.Group>

                    {/* NOTES & TERMS */}
                    <Form.Group className="mb-3" controlId="notes">
                      <Form.Label>Notes / Special Instructions</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="notes"
                        value={data.notes}
                        onChange={handleInputChange}
                        placeholder="Gate width, pets, stairs, special timing, etc."
                      />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="acceptTerms">
                      <Form.Check
                        type="checkbox"
                        checked={data.acceptTerms}
                        onChange={handleTermsChange}
                        label={
                          <span className="small">
                            I have read and agree to the rental terms, weather
                            policy, and safety rules. I understand this is a
                            reservation request and not a confirmed booking
                            until I receive a confirmation email.
                          </span>
                        }
                        required
                      />
                    </Form.Group>

                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      className="w-100"
                    >
                      Submit Reservation Request
                    </Button>
                    <p className="mt-2 mb-0 small text-muted">
                      We typically respond within one business day with your
                      confirmation and payment link.
                    </p>
                  </Form>
                </Card.Body>
              </Card>
            </Col>

            {/* RIGHT: SUMMARY */}
            <Col lg={4} id="summary">
              <Card className="shadow-sm border-0 mb-3">
                <Card.Body>
                  <h5 className="fw-bold mb-3">
                    Reservation Summary <CashCoin className="ms-1" />
                  </h5>
                  <ListGroup variant="flush" className="mb-3">
                    <ListGroup.Item>
                      <div className="small text-muted">Selected Unit</div>
                      <div className="fw-bold">
                        {data.unitType === "classic" && "Classic Castle"}
                        {data.unitType === "combo" && "Slide Combo Castle"}
                        {data.unitType === "obstacle" && "Mega Obstacle Course"}
                        {!data.unitType && (
                          <span className="text-muted">
                            No unit selected yet
                          </span>
                        )}
                      </div>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <div className="small text-muted">Event Date & Time</div>
                      <div className="fw-bold">
                        {data.eventDate || "Not set"}{" "}
                        {data.startTime && `• ${data.startTime}`}
                        {data.endTime && ` – ${data.endTime}`}
                      </div>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <div className="small text-muted">Location</div>
                      <div className="fw-bold">
                        {data.city || "City not set"}{" "}
                        {data.zip && `(${data.zip})`}
                      </div>
                      <div className="small text-muted">
                        {data.address || "Address not set"}
                      </div>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <div className="small text-muted">Add-ons</div>
                      {data.addOns.length === 0 ? (
                        <span className="text-muted">
                          No add-ons selected yet
                        </span>
                      ) : (
                        <ul className="small mb-0 ps-3">
                          {data.addOns.map((id) => {
                            const item = addOnOptions.find(
                              (opt) => opt.id === id
                            );
                            if (!item) return null;
                            return (
                              <li key={id}>
                                {item.label} (${item.price})
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </ListGroup.Item>
                  </ListGroup>

                  <div className="border-top pt-3">
                    <div className="d-flex justify-content-between mb-1">
                      <span className="small text-muted">Estimated Total</span>
                      <strong>${estimatedTotal || 0}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span className="small text-muted">
                        Estimated Deposit (25%)
                      </span>
                      <strong>${estimatedDeposit || 0}</strong>
                    </div>
                    <Form.Text className="text-muted small">
                      Final total may change based on distance, setup details,
                      and discounts. We&apos;ll confirm in your quote.
                    </Form.Text>
                  </div>
                </Card.Body>
              </Card>

              <Card className="shadow-sm border-0">
                <Card.Body>
                  <h6 className="fw-bold mb-2">
                    Questions? We&apos;re here to help.
                  </h6>
                  <p className="small text-muted mb-2">
                    Not sure which unit to pick, or have special requirements?
                    Reach out and we can walk you through options.
                  </p>
                  <div className="small mb-1">
                    <TelephoneFill className="me-1" />
                    (800) 555-1234
                  </div>
                  <div className="small mb-1">
                    <EnvelopeFill className="me-1" />
                    hello@happybounce.com
                  </div>
                  <div className="small mb-1">
                    <ShieldCheck className="me-1 text-success" />
                    Fully insured • Cleaned &amp; sanitized after every event
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FOOTER */}
      <footer className="py-3 bg-dark text-light small">
        <Container>
          <Row className="align-items-center gy-2">
            <Col md={6}>
              © {new Date().getFullYear()} HappyBounce Rentals. All rights
              reserved.
            </Col>
            <Col md={6} className="text-md-end">
              Safe, insured, and family-owned bounce house rentals.
            </Col>
          </Row>
        </Container>
      </footer>
    </>
  );
};

export default BounceHouseReservationPage_2025_11_21_chat;
